// ===========================================================================
// THE TURNSTILE WIDGET, on signup, password reset and the public application
// form (`/apply`, S98) only.
//
// `07-authentication.md` §5 keeps it off login deliberately, so this component
// is imported by exactly the screens that carry those three forms and there is
// no generic "protect this form" wrapper — a wrapper is how it would end up on
// login.
//
// ★ A TOKEN IS SINGLE USE. Cloudflare invalidates it once siteverify has seen
// it, so a form that failed for any other reason (a taken email, a weak
// password) MUST reset the widget before the person presses the button again,
// or their second attempt fails with a stale token and the message they get
// describes the wrong problem. `reset()` is why this exposes a ref rather than
// just a callback.
//
// WITH NO SITE KEY IT RENDERS NOTHING AND REPORTS NO TOKEN. That is the local
// developer's build, and it is the API — not the browser — that decides whether
// a missing token matters: `api/src/auth/turnstile.ts` challenges every
// environment except `local`. A widget that blocked the button when it could
// not configure itself would make the SPA unusable the moment an env var was
// forgotten, and would be a client-side control pretending to be a server one.
// ===========================================================================
import { useEffect, useRef } from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
};
declare global {
  // eslint-disable-next-line no-var
  var turnstile: TurnstileApi | undefined;
}

export function turnstileSiteKey(): string | undefined {
  const key = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  return key && key.length > 0 ? key : undefined;
}

/** Loaded once per document, however many widgets ask for it. */
let scriptPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (globalThis.turnstile) return resolve();
    const el = document.createElement("script");
    el.src = SCRIPT_SRC;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error("turnstile script did not load"));
    document.head.appendChild(el);
  });
  return scriptPromise;
}

export type ChallengeHandle = { reset: () => void };

export function Challenge({
  onToken,
  handle,
}: {
  /** Called with the token, or with null when it expires or errors. */
  onToken: (token: string | null) => void;
  handle?: React.MutableRefObject<ChallengeHandle | null>;
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);
  const key = turnstileSiteKey();

  // ★ THE WIDGET IS MOUNTED ONCE AND CALLS BACK MUCH LATER, so a callback
  // captured at mount would be a closure over THAT render's state. `SignUp`
  // passes a setter and would not notice; `SignIn` passes an arrow that reads
  // the typed email, and someone who corrects their address after the challenge
  // appears would have the reset sent to the old one. Holding the latest
  // callback in a ref removes the whole class rather than the one path.
  const latest = useRef(onToken);
  latest.current = onToken;

  useEffect(() => {
    if (!key || !host.current) return;
    let cancelled = false;
    const el = host.current;

    loadScript()
      .then(() => {
        if (cancelled || !globalThis.turnstile) return;
        widgetId.current = globalThis.turnstile.render(el, {
          sitekey: key,
          callback: (token: string) => latest.current(token),
          "expired-callback": () => latest.current(null),
          "error-callback": () => latest.current(null),
        });
        if (handle) {
          handle.current = {
            reset: () => {
              if (widgetId.current && globalThis.turnstile) {
                globalThis.turnstile.reset(widgetId.current);
                latest.current(null);
              }
            },
          };
        }
      })
      .catch(() => latest.current(null));

    return () => {
      cancelled = true;
      if (widgetId.current && globalThis.turnstile) globalThis.turnstile.remove(widgetId.current);
      widgetId.current = null;
      if (handle) handle.current = null;
    };
    // The key is build-time constant and the callbacks are stable for the life
    // of the form, so this mounts one widget and never re-renders it — a
    // re-render would hand the person a fresh puzzle mid-typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!key) return null;
  return <div ref={host} className="turnstile" />;
}
